import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment.development';
import { STORAGE_ENUM } from '../../../auth/enums/storage.enum';
import { AuthService } from '../../../auth/services/auth/auth.service';
import { SessionService } from '../../../auth/services/session/session.service';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';
import { AreaPhotosResponse, AreasResponse, InspectionDataResponse, InspectionPhotoResponse, InspectionResponse, ZoneResponse } from '../../models/areas.model';

@Injectable({
  providedIn: 'root'
})
export class AreasService {
  private _http = inject(HttpClient);
  private _authService = inject(AuthService);
  private _sessionService = inject(SessionService);
  private _localStorage = inject(LocalStorageService);

  private _baseUrl = `${environment.apiUrl}/administrative/mip`;
  private _eventsUrl = `${environment.apiUrl}/administrative/events`;
  private _pestControlUrl = `${environment.apiUrl}/administrative/pest-control`;

  getEvents(filters: any): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService!.Customer.id;
    let eventsUrl = `${this._eventsUrl}/?customer=${customerId}`;

    if (isMainCustomer) {
      eventsUrl += `&customer__main_customer=${customerId}`;
    }

    if (filters.page) {
      eventsUrl += `&page=${filters.page}`;
    }

    if (filters.page_size) {
      eventsUrl += `&page_size=${filters.page_size}`;
    }

    if (filters.search) {
      eventsUrl += `&search=${filters.search}`;
    }

    if (filters.initial_date) {
      eventsUrl += `&initial_date=${filters.initial_date}`;
    }

    if (filters.final_date) {
      eventsUrl += `&final_date=${filters.final_date}`;
    }

    if (filters.status__name) {
      eventsUrl += `&status__name=${filters.status__name}`;
    }

    return this._http.get<any>(eventsUrl, { headers: { Authorization: `Bearer ${token}` } });
  }

  getAreas(filters: any): Observable<{
    items: AreasResponse[],
    total: number,
    page: number,
    page_size: number
  }> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error getting areas'));
    }

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService.Customer.id;

    let uri = `${this._baseUrl}/station-count-data/`;
    uri += isMainCustomer
      ? `?event__customer__main_customer=${customerId}`
      : `?event__customer=${customerId}`;

    if (filters.event) {
      uri += `&event=${filters.event}`;
    }

    if (filters.search) {
      uri += `&search=${filters.search}`;
    }

    if (filters.page) {
      uri += `&page=${filters.page}`;
    }

    if (filters.page_size) {
      uri += `&page_size=${filters.page_size}`;
    }

    return this._http.get<{ items: AreasResponse[], total: number, page: number, page_size: number }>(
      uri,
      { headers }
    );
  }

  getAreasPhotos(areaId: string): Observable<AreaPhotosResponse[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    let uri = `${this._baseUrl}/station-count-photo/?station=${areaId}`;

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.get<AreaPhotosResponse[]>(
      uri,
      { headers }
    );
  }

  getInspectionsForms(filters: any): Observable<{
    items: InspectionResponse[],
    total: number,
    page: number,
    page_size: number
  }> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error getting inspections'));
    }

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService.Customer.id;
    let uri = `${this._baseUrl}/inspection-forms/?event__customer=${customerId}`;

    if (isMainCustomer) {
      uri += `&event__customer__main_customer=${customerId}`;
    }

    if (filters.search) {
      uri += `&search=${filters.search}`;
    }

    if (filters.page) {
      uri += `&page=${filters.page}`;
    }

    if (filters.page_size) {
      uri += `&page_size=${filters.page_size}`;
    }

    return this._http.get<{ items: InspectionResponse[], total: number, page: number, page_size: number }>(
      uri,
      { headers }
    );
  }

  getInspectionsFormsByEvent(filters: any): Observable<{
    items: InspectionDataResponse[], total: number, page: number, page_size: number
  }> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    let uri = `${this._baseUrl}/inspection-forms-data/?inspection_form__event=${filters.id}`;

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (filters.page) {
      uri += `&page=${filters.page}`;
    }

    if (filters.page_size) {
      uri += `&page_size=${filters.page_size}`;
    }

    return this._http.get<{ items: InspectionDataResponse[], total: number, page: number, page_size: number }>(
      uri,
      { headers }
    );
  }

  getInspectionsData(formId: string): Observable<InspectionDataResponse[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    let uri = `${this._baseUrl}/inspection-forms-data/?inspection_form=${formId}`;

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.get<InspectionDataResponse[]>(
      uri,
      { headers }
    );
  }

  getInspectionPhotos(formId: string): Observable<InspectionPhotoResponse[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    let uri = `${this._baseUrl}/inspection-forms-photo/?inspection_form=${formId}`;

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.get<InspectionPhotoResponse[]>(
      uri,
      { headers }
    );
  }

  getEventData(eventId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    let uri = `${this._eventsUrl}/${eventId}/`;

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.get<any>(
      uri,
      { headers }
    );
  }

  getZone(zoneId: string): Observable<ZoneResponse> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    let uri = `${this._pestControlUrl}/zones/${zoneId}/`;

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.get<ZoneResponse>(
      uri,
      { headers }
    );
  }
}
